import { GenericPortalError } from "../components/error-display/PortalError";

import { restService } from "./RestService";
import { restServiceToLib } from "./RestServiceToLib";
const X_MSTR_AUTH_TOKEN = "X-MSTR-AuthToken";

class AuthenticationService {
  /** LOGIN */
  async login(config, username, password) {
    const url = `${config.backendServiceURL}api/auth/login`;
    const body = {
      username,
      password,
      loginMode: 1,
      locale: config.locale,
      metadataLocale: config.locale,
      warehouseDataLocale: config.locale,
      displayLocale: config.locale,
      messagesLocale: config.locale,
      numberLocale: config.locale,
    };

    const res = await restService.post(url, body);
    const token = res.headers.get(X_MSTR_AUTH_TOKEN);

    if (token) {
      return token;
    } else {
      const error = await res.json();

      throw GenericPortalError("INVALID_CREDENTIALS", "error", error);
    }
  }

  async getAuthToken(config) {
    let options = {
      method: "GET",
      credentials: "include", // Including cookie
      mode: "cors", // Setting as cors mode for cross origin
      headers: { "content-type": "application/json" },
    };

    return fetch(config.backendServiceURL + "api/auth/token", options)
      .then((response) => {
        if (response.ok) {
          return response.headers.get("x-mstr-authtoken");
        } else {
          return null;
        }
      })
      .catch((_error) => {
        // Log the error if failed
        return null;
      });
  }

  /** LOGIN SAML */
  async loginSAML(config) {
    const startTime = Date.now();
    let newTab = window.open(
      config.backendServiceURL + "auth/login-dialog.jsp?loginMode=" + config.loginMode,
      "newwin",
      "height=" + window.screen.availHeight / 2 + "px,width=" + window.screen.availWidth / 2 + "px",
    );

    async function getAuthToken(config) {
      let options = {
        method: "GET",
        credentials: "include", // Including cookie
        mode: "cors", // Setting as cors mode for cross origin
        headers: { "content-type": "application/json" },
      };

      return fetch(config.backendServiceURL + "api/auth/token", options)
        .then((response) => {
          if (response.ok) {
            return response.headers.get("x-mstr-authtoken");
          } else {
            return null;
          }
        })
        .catch((_error) => {
          // Log the error if failed
          return null;
        });
    }
    async function checkLogin(config, newTab, startTime, timeout, interval) {
      const currentTime = Date.now();

      let token = await getAuthToken(config).catch((_error) => {
        return null;
      });

      // If auth token is valid return it
      if (!!token) {
        if (newTab && !newTab.closed) {
          newTab.close();
        }

        return token;
      } else if (currentTime - startTime > timeout) {
        if (newTab && !newTab.closed) {
          newTab.close();
        }

        return null;
      } else {
        if (newTab.closed) {
          return null;
        }

        // Check if the user logged in already every interval milliseconds
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve(checkLogin(config, newTab, startTime, timeout, interval));
          }, interval);
        });
      }
    }

    const token = await checkLogin(config, newTab, startTime, config.loginTimeout * 1000, 5000);

    return token;
  }

  async createIdentityToken(config, authToken) {
    const url = `${config.backendServiceURL}api/auth/identityToken`;

    const options = {
      method: "POST",
      credentials: "include", // Including cookie
      mode: "cors", // Setting as cors mode for cross origin
      headers: {
        "content-type": "application/json",
        accept: "application/json",
        "X-MSTR-AuthToken": authToken,
      },
    };

    try {
      let response = await fetch(url, options);

      if (response.ok) {
        return response.json();
      } else {
        const res = await response.json();

        if (response.status === 401) {
          throw GenericPortalError("INVALID_CREDENTIALS", "error", res);
        } else {
          throw GenericPortalError("ERROR_INSTANCE_CREATION", "error", res);
        }
      }
    } catch (e) {}
  }

  /** DELEGATE - TRANSFORM IDENTITY TOKEN TO MSTR AUTH TOKEN*/
  async delegate(config, identityToken) {
    const url = `${config.backendServiceURL}api/auth/delegate`;
    const body = {
      loginMode: config.loginMode,
      identityToken: identityToken,
    };
    const res = await restServiceToLib.post(url, body);
    const authToken = res.headers.get("X-MSTR-AuthToken");

    if (res && res.ok && authToken) {
      return authToken;
    }

    return null;
  }

  /** KEEP SESSION ALIVE  **/
  async isSessionAlive(config, token) {
    try {
      const url = `${config.backendServiceURL}api/sessions`;
      const res = await restService.put(url, null, token);

      if (res && res.ok) {
        const renewedToken = res.headers.get(X_MSTR_AUTH_TOKEN);

        return renewedToken;
      } else {
        const error = await res.json();

        throw GenericPortalError("TIMEOUT", "error", error);
      }
    } catch (e) {
      throw GenericPortalError("TIMEOUT", "error", e);
    }
  }

  /** LOGOUT **/
  async logout(config, token) {
    const url = `${config.backendServiceURL}api/auth/logout`;
    const res = await restService.post(url, null, token);

    if (res && res.ok) {
      return res.ok;
    } else {
      const error = await res.json();

      throw GenericPortalError("TIMEOUT", "error", error);
    }

    return null;
  }

  async getUserGroups(config, token) {
    let userInfo = await this.getUserInfo(config, token);
    const url = `${config.backendServiceURL}api/users/${userInfo.id}/memberships`;
    const res = await restService.get(url, token);

    if (res && res.ok) {
      const results = await res.json();

      return results;
    } else {
      const error = await res.json();

      throw GenericPortalError("ERROR_RETRIEVING_CONFIGADMIN", "error", error);
    }
  }

  async getUserInfo(config, token) {
    const url = `${config.backendServiceURL}api/sessions/userInfo`;
    const res = await restService.get(url, token);

    if (res && res.ok) {
      const results = await res.json();

      return results;
    } else {
      const error = await res.json();

      throw GenericPortalError("ERROR_RETRIEVING_CONFIGADMIN", "error", error);
    }
  }
}

export const authenticationService = new AuthenticationService();
